/* eslint-disable import/no-unresolved */
import 'swiper/css';
import 'swiper/css/virtual';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
/* eslint-enable import/no-unresolved */
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  Navigation, Virtual, Pagination, Autoplay, Thumbs
} from 'swiper';
import {
  motion, LayoutGroup, AnimatePresence
} from 'framer-motion';
import { TRACK_TESTIMONIAL_CLICKED } from 'constants';
import { AnalyticsConsumer } from 'contexts/AnalyticsProvider';
import Backdrop from '@mui/material/Backdrop';
import IconButton from '@mui/material/IconButton';
import classNames from 'classnames';
import Card from '@mui/material/Card';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Image from 'components/Image';
import makeClasses from 'hooks/useClasses';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import React, { useRef, useState } from 'react';
import RichTextRenderer from 'components/Text/RichTextRenderer';
import styles from 'styles/jss/components/testimonialStyle';
import Typography from '@mui/material/Typography';

const VARIANTS = {
  modal: {
    visible: {
      width: 400,
      transition: { duration: 1, type: 'spring' }
    },
    hidden: { scale: 1, width: 300 }
  },
  card: {
    visible: {
      scale: 1.1,
      transition: { duration: 1, type: 'spring' }
    },
    hidden: { scale: 1, opacity: 0 }
  },
  cardMedia: {
    initial: {
      height: 350,
      transition: { duration: 1, type: 'spring' }
    },
    expanded: { height: '70%' }
  }
};

const Testimonial = ({ testimonial, playVideo, handleClose }) => {
  const classes = makeClasses(styles);
  return (
    <Card
      elevation={6}
      sx={{
        borderRadius: 2,
        width: '100%',
        // Safari hack to get overflow clipping
        transform: 'translate3d(0, 0, 0)'
      }}
    >
      {playVideo && (
        <IconButton onClick={handleClose} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      )}
      <motion.div
        className="cardMedia"
        layoutId={`cardMedia-${testimonial.entryId}`}
        variants={VARIANTS.cardMedia}
        animate={playVideo ? 'expanded' : 'initial'}
        exit="hidden"
      >
        {testimonial.fileType === 'video' ? (
          <>
            <ReactPlayer
              className={classNames(classes.videoPlayer, {
                isExpanded: playVideo
              })}
              url={testimonial.file}
              loop
              playing={playVideo}
              controls={playVideo}
              width="100%"
              height="100%"
            />
            <div
              className={classNames(classes.playButton, {
                show: !playVideo
              })}
            >
              <IconButton>
                <PlayCircleOutlineIcon />
              </IconButton>
            </div>
          </>
        ) : (
          <Image
            src={testimonial.file}
            alt={testimonial.description}
            layout="fill"
            objectFit="contain"
            // priority={i === 0}
            sizes="(min-width: 900px) 700px,
                  400px"
          />
        )}
      </motion.div>
      <motion.div
        className={classes.testimonial}
        layoutId={`cardContent-${testimonial.entryId}`}
      >
        <Typography
          variant="body2"
          className="praise"
          color="text.secondary"
        >
          <RichTextRenderer
            data={testimonial.testimonial}
          />
        </Typography>
        <Typography
          variant="body2"
          component="div"
          className="author"
        >
          {testimonial.author.name}
        </Typography>
      </motion.div>
    </Card>
  );
};

Testimonial.propTypes = {
  playVideo: PropTypes.bool.isRequired,
  testimonial: PropTypes.shape().isRequired,
  handleClose: PropTypes.func.isRequired
};

const Testimonials = ({ testimonials }) => {
  const classes = makeClasses(styles);
  const swiperRef = useRef(null);
  const [card, setCard] = useState(false);
  const { track } = AnalyticsConsumer();

  const handleClick = (testimonial) => {
    setCard(testimonial);
    track(TRACK_TESTIMONIAL_CLICKED, { testimonial: testimonial.author.name });
  };

  return (
    <LayoutGroup>
      <Grid
        container
        item
        maxWidth="lg"
        spacing={5}
        sx={{ px: 5 }}
      >
        <Grid
          item
          xs={12}
        >
          <Swiper
            ref={swiperRef}
            className={classes.slideSwiper}
            modules={[Navigation, Virtual, Pagination, Autoplay, Thumbs]}
            navigation={{
              prevEl: '#prev_slide',
              nextEl: '#next_slide'
            }}
            autoplay={{
              delay: 5000,
              disableOnInteraction: true
            }}
            lazy
            pagination={{
              el: '.swiper-custom-pagination',
              clickable: true
            }}
            breakpoints={{
              // when window width is >= 320px
              320: {
                slidesPerView: 1,
                spaceBetween: 10
              },
              // when window width is >= 480px
              640: {
                slidesPerView: 2,
                spaceBetween: 10
              },
              // when window width is >= 640px
              900: {
                slidesPerView: 3, // testimonials.length < 4 ? testimonials.length : 4,
                spaceBetween: 10
              }
            }}
          >
            {testimonials.map((slide, i) => (
              <SwiperSlide
                key={slide.file}
                virtualIndex={i}
                className={classes.slide}
              >
                <motion.div
                  layout
                  className={classes.slideCard}
                  key={slide.entryId}
                  layoutId={slide.entryId}
                  onClick={() => handleClick(slide)}
                >
                  <Testimonial testimonial={slide} playVideo={false} />
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
          <Box
            className={classes.paginationBox}
          >
            <div className="swiper-custom-pagination" style={{ height: 40 }} />
          </Box>
        </Grid>
      </Grid>
      <AnimatePresence>
        {card && (
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open
            onClick={() => setCard(false)}
          >
            <motion.div
              layout
              layoutId={card.entryId}
              className={classNames(classes.slideCard, 'expanded')}
              variants={VARIANTS.modal}
              animate="visible"
              exit="hidden"
            >
              <Testimonial
                testimonial={card}
                playVideo
                handleClose={() => setCard(false)}
              />
            </motion.div>
          </Backdrop>
        )}
      </AnimatePresence>
    </LayoutGroup>
  );
};

Testimonials.propTypes = {
  testimonials: PropTypes.arrayOf(PropTypes.shape()).isRequired
};

export default Testimonials;
